import { type FieldBlockAbstract } from '@lighthouse/core'
import type { FieldInputProps, UseUploadFileSParameter } from '@lighthouse/shared'
import { FieldInput, flexMinSizeStyle, getCursorStyle, useApplicationContext } from '@lighthouse/shared'
import type { UploadyProps } from '@rpldy/uploady'
import React, { memo } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

interface FieldBlockProps extends Omit<FieldInputProps, 'config' | 'mode'> {
    uploadyOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    blockData: FieldBlockAbstract
    richTextUploadOptions?: UploadyProps
    blockType: 'field' | 'form'
}

const SCxContainer = styled(AnimationDecorators)`
    width: 100%;
    height: 100%;
    /* overflow: hidden; */
    /* padding: 6px 5px 4px 4px; */
`
const FieldBlock = React.forwardRef<HTMLDivElement, FieldBlockProps>((props, ref) => {
    const {
        id,
        blockData,
        blockType,
        value,
        uploadyOptions,
        language,
        dataSource,
        record,
        richTextUploadOptions,
        error,
        onChange,
        onBlur,
        onFocus,
        onKeyDown,
        ...rest
    } = props
    const { config, title, id: blockId } = blockData
    const { breakPoint, canEdit, cursor, anchor } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)

    const { appId = '' } = useApplicationContext()

    return (
        <SCxContainer
            ref={ref}
            data-stop-action-propagation={canEdit ? true : undefined}
            blockId={blockId}
            blockName={title}
            animation={breakPoint?.animation}
            id={effectiveAnchorId}
            {...rest}
            style={{ ...flexMinSizeStyle, overflow: breakPoint.size.overflow, ...rest.style, ...getCursorStyle(appId, cursor) }}
        >
            <FieldInput
                id={id}
                config={{
                    ...config,
                    richTextUploadOptions,
                    uploadyOptions,
                    title
                }}
                mode={blockType === 'field' ? 'normal' : 'datasource'}
                dataSource={dataSource}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                language={language}
                record={record}
                error={error}
                {...rest}
            />
        </SCxContainer>
    )
})

export default memo(FieldBlock)
